import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import CallOutlinedIcon from '@mui/icons-material/CallOutlined'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import PhoneForwardedOutlinedIcon from '@mui/icons-material/PhoneForwardedOutlined'
import SendOutlinedIcon from '@mui/icons-material/SendOutlined'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  dispatchSupportCustomCommunication,
  getSupportCustomCommunicationsBootstrap,
} from 'src/supportPortal/services/customCommunicationService'

const ENTRY_ROUTE = '/support-home/important-actions/custom-communications'
const SECTION_ROUTES = {
  overview: ENTRY_ROUTE,
  call: `${ENTRY_ROUTE}/call`,
  whatsapp: `${ENTRY_ROUTE}/whatsapp`,
}
const EMPTY_FORM = {
  toPhoneNumber: '',
  languageCode: 'auto',
  message: '',
}

function getSectionIdFromPath(pathname) {
  if (pathname?.endsWith('/call')) {
    return 'call'
  }
  if (pathname?.endsWith('/whatsapp')) {
    return 'whatsapp'
  }
  return 'overview'
}

function getSectionCopy(sectionId) {
  if (sectionId === 'call') {
    return {
      title: 'Call',
      description: 'Use the configured outcall client to speak the same text to the phone number you enter.',
    }
  }
  if (sectionId === 'whatsapp') {
    return {
      title: 'Whatsapp',
      description: 'Send ad hoc WhatsApp text from the selected sender number with warm-window and template fallback handled by the backend.',
    }
  }
  return {
    title: 'Custom Communications',
    description: 'Management-only tools for ad hoc call and WhatsApp testing.',
  }
}

function getSectionIcon(sectionId, sx = {}) {
  if (sectionId === 'call') {
    return <PhoneForwardedOutlinedIcon sx={sx} />
  }
  if (sectionId === 'whatsapp') {
    return <WhatsAppIcon sx={sx} />
  }
  return <HubOutlinedIcon sx={sx} />
}

function getSectionAccent(sectionId) {
  if (sectionId === 'call') {
    return '#1d4ed8'
  }
  if (sectionId === 'whatsapp') {
    return '#15803d'
  }
  return '#7c3aed'
}

function getClientAccent(sectionId, client = {}) {
  if (sectionId === 'call') {
    return client?.providerType === 'plivo' ? '#0f766e' : '#1d4ed8'
  }
  if (client?.id === 'twilio-mentor-support-number') {
    return '#7c3aed'
  }
  return '#15803d'
}

function getConfiguredClientCount(section = {}) {
  return Array.isArray(section?.clients)
    ? section.clients.filter((client) => client?.configured).length
    : 0
}

function hasValidPhoneNumber(value) {
  return String(value || '')
    .replace(/\D/g, '')
    .length >= 10
}

function formatChipLabel(prefix, value) {
  if (!value && value !== 0) {
    return null
  }
  return `${prefix}: ${value}`
}

function getResolvedSpeechSummary(dispatch = {}) {
  if (!dispatch?.resolvedLanguageLabel) {
    return null
  }
  if (dispatch?.requestedLanguageCode === 'auto') {
    return `${dispatch.resolvedLanguageLabel} (Auto)`
  }
  return dispatch.resolvedLanguageLabel
}

function OverviewSectionCard({ section, onOpen }) {
  const accent = getSectionAccent(section?.id)
  const totalClients = Array.isArray(section?.clients) ? section.clients.length : 0
  const configuredClients = getConfiguredClientCount(section)

  return (
    <Card
      onClick={onOpen}
      sx={{
        cursor: 'pointer',
        borderRadius: 4,
        border: '1px solid',
        borderColor: alpha(accent, 0.18),
        background: `linear-gradient(180deg, ${alpha(accent, 0.1)} 0%, rgba(255, 255, 255, 0.98) 35%)`,
        boxShadow: `0 16px 38px ${alpha(accent, 0.14)}`,
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 22px 44px ${alpha(accent, 0.2)}`,
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                color: '#fff',
                background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.82)} 100%)`,
                boxShadow: `0 10px 24px ${alpha(accent, 0.28)}`,
              }}
            >
              {getSectionIcon(section?.id, { fontSize: 24 })}
            </Box>
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {section?.label || getSectionCopy(section?.id).title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getSectionCopy(section?.id).description}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              size="small"
              label={`${configuredClients}/${totalClients} configured`}
              sx={{
                fontWeight: 700,
                backgroundColor: alpha(accent, 0.12),
                color: accent,
              }}
            />
            {section?.route ? <Chip size="small" label={section.route.replace('/support-home', '')} /> : null}
          </Stack>

          <Button
            variant="contained"
            onClick={onOpen}
            endIcon={<SendOutlinedIcon />}
            sx={{
              alignSelf: 'flex-start',
              background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.78)} 100%)`,
            }}
          >
            Open {section?.label || getSectionCopy(section?.id).title}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  )
}

function DispatchSummary({ sectionId, dispatch }) {
  if (!dispatch) {
    return null
  }

  const isCall = sectionId === 'call'

  return (
    <Alert
      severity="success"
      variant="outlined"
      sx={{
        mt: 2,
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
    >
      <Stack spacing={1.25}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {isCall ? 'Latest outcall request' : 'Latest WhatsApp dispatch'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isCall
            ? `From ${dispatch?.fromPhoneNumber || 'configured caller'} to ${dispatch?.toPhoneNumber || '-'}`
            : `From ${dispatch?.senderPhoneNumber || '-'} to ${dispatch?.toPhoneNumber || '-'}`
          }
        </Typography>
        {dispatch?.messagePreview ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic', wordBreak: 'break-word' }}>
            &quot;{dispatch.messagePreview}&quot;
          </Typography>
        ) : null}
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {isCall ? (
            <>
              {formatChipLabel('Provider', String(dispatch?.providerType || '').toUpperCase()) ? (
                <Chip size="small" label={formatChipLabel('Provider', String(dispatch?.providerType || '').toUpperCase())} />
              ) : null}
              {formatChipLabel('Request', dispatch?.requestId) ? (
                <Chip size="small" label={formatChipLabel('Request', dispatch?.requestId)} />
              ) : null}
              {getResolvedSpeechSummary(dispatch) ? (
                <Chip size="small" label={formatChipLabel('Speech', getResolvedSpeechSummary(dispatch))} />
              ) : null}
              {formatChipLabel('Voice', dispatch?.resolvedVoice) ? (
                <Chip size="small" label={formatChipLabel('Voice', dispatch?.resolvedVoice)} />
              ) : null}
            </>
          ) : (
            <>
              {formatChipLabel('Mode', dispatch?.deliveryMode) ? (
                <Chip size="small" label={formatChipLabel('Mode', dispatch?.deliveryMode)} />
              ) : null}
              <Chip
                size="small"
                label={dispatch?.warmWindowOpen ? 'Warm window open' : 'Template fallback used'}
              />
              {formatChipLabel('Template', dispatch?.templateSid) ? (
                <Chip size="small" label={formatChipLabel('Template', dispatch?.templateSid)} />
              ) : null}
              {formatChipLabel('Message SID', dispatch?.messageSid) ? (
                <Chip size="small" label={formatChipLabel('Message SID', dispatch?.messageSid)} />
              ) : null}
            </>
          )}
        </Stack>
      </Stack>
    </Alert>
  )
}

function ClientCard({
  client,
  form,
  sectionId,
  sending,
  dispatch,
  onFieldChange,
  onSubmit,
}) {
  const accent = getClientAccent(sectionId, client)
  const isCall = sectionId === 'call'
  const callerNumbers = Array.isArray(client?.callerNumbers) ? client.callerNumbers : []
  const languageOptions = Array.isArray(client?.languageOptions) ? client.languageOptions : []
  const selectedLanguageCode = form?.languageCode || client?.defaultLanguageCode || 'auto'

  return (
    <Card
      sx={{
        borderRadius: 4,
        border: '1px solid',
        borderColor: alpha(accent, 0.16),
        background: `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, rgba(255, 255, 255, 0.98) 38%)`,
        boxShadow: `0 18px 40px ${alpha(accent, 0.14)}`,
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  display: 'grid',
                  placeItems: 'center',
                  color: '#fff',
                  background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.82)} 100%)`,
                }}
              >
                {isCall ? (
                  <CallOutlinedIcon sx={{ fontSize: 23 }} />
                ) : (
                  <WhatsAppIcon sx={{ fontSize: 23 }} />
                )}
              </Box>
              <Stack spacing={0.5}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {client?.label || (isCall ? 'Outcall Client' : 'Whatsapp Sender')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isCall
                    ? 'The message below will be spoken during the call.'
                    : 'The backend decides free-form vs template based on the exact 24-hour sender window.'}
                </Typography>
              </Stack>
            </Stack>
            <Chip
              size="small"
              label={client?.configured ? 'Configured' : 'Needs setup'}
              sx={{
                fontWeight: 700,
                backgroundColor: client?.configured ? alpha('#16a34a', 0.12) : alpha('#dc2626', 0.12),
                color: client?.configured ? '#15803d' : '#b91c1c',
              }}
            />
          </Stack>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {formatChipLabel('Provider', String(client?.providerType || '').toUpperCase()) ? (
              <Chip size="small" label={formatChipLabel('Provider', String(client?.providerType || '').toUpperCase())} />
            ) : null}
            {formatChipLabel('Role', client?.actorRole) ? (
              <Chip size="small" label={formatChipLabel('Role', client?.actorRole)} />
            ) : null}
            {!isCall && formatChipLabel('From', client?.senderPhoneNumber) ? (
              <Chip size="small" label={formatChipLabel('From', client?.senderPhoneNumber)} />
            ) : null}
          </Stack>

          {isCall && callerNumbers.length > 0 ? (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                Caller number pool
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                {callerNumbers.map((callerNumber) => (
                  <Chip key={callerNumber} size="small" label={callerNumber} />
                ))}
              </Stack>
            </Box>
          ) : null}

          {!isCall && client?.warmWindowRule ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Warm window rule
              </Typography>
              <Typography variant="body2">{client.warmWindowRule}</Typography>
            </Alert>
          ) : null}

          {!isCall && client?.templateFallbackSid ? (
            <Typography variant="caption" color="text.secondary">
              Template fallback SID: {client.templateFallbackSid}
            </Typography>
          ) : null}

          {!client?.configured ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {isCall
                ? 'This outcall client is not fully configured yet. Complete provider setup before sending calls.'
                : 'This sender is not fully configured yet. Complete WhatsApp setup before sending messages.'}
            </Alert>
          ) : null}

          <Divider />

          <Stack spacing={2}>
            <TextField
              label="Phone Number"
              placeholder="+919999999999"
              value={form?.toPhoneNumber || ''}
              onChange={(event) => onFieldChange(client?.id, 'toPhoneNumber', event.target.value)}
              fullWidth
            />
            {isCall && languageOptions.length > 0 ? (
              <TextField
                select
                label="Speech Language"
                value={selectedLanguageCode}
                onChange={(event) => onFieldChange(client?.id, 'languageCode', event.target.value)}
                helperText={client?.autoLanguageRule || 'Choose the speech language for this call.'}
                fullWidth
              >
                {languageOptions.map((option) => (
                  <MenuItem key={option?.id || option?.label} value={option?.id || 'auto'}>
                    {option?.label || option?.id || 'Auto'}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            <TextField
              label={isCall ? 'Message To Speak' : 'Message To Send'}
              placeholder={
                isCall
                  ? 'This is a custom call test from AUMHUM support.'
                  : 'This is a custom WhatsApp test from AUMHUM support.'
              }
              value={form?.message || ''}
              onChange={(event) => onFieldChange(client?.id, 'message', event.target.value)}
              minRows={4}
              multiline
              fullWidth
            />
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="caption" color="text.secondary">
                {isCall
                  ? 'The voice provider will read this text out during the outcall.'
                  : 'Delivery mode will be free-form only when the recipient has messaged this exact sender in the last 24 hours.'}
              </Typography>
              <Button
                variant="contained"
                disabled={!client?.configured || sending}
                onClick={() => onSubmit(client)}
                endIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
                sx={{
                  minWidth: { xs: '100%', sm: 180 },
                  background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.8)} 100%)`,
                }}
              >
                {sending ? 'Sending...' : isCall ? 'Place Call' : 'Send Whatsapp'}
              </Button>
            </Stack>
          </Stack>

          <DispatchSummary sectionId={sectionId} dispatch={dispatch} />
        </Stack>
      </CardContent>
    </Card>
  )
}

export default function CustomCommunicationsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const currentView = getSectionIdFromPath(location.pathname)
  const [bootstrap, setBootstrap] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [formsByClientId, setFormsByClientId] = useState({})
  const [resultsByClientId, setResultsByClientId] = useState({})
  const [sendingClientId, setSendingClientId] = useState(null)

  const entryPoint = bootstrap?.entryPoint || {
    label: 'Custom Communications',
    route: ENTRY_ROUTE,
    summary: 'Management-only tools for ad hoc call and WhatsApp testing.',
  }
  const sections = Array.isArray(bootstrap?.sections) ? bootstrap.sections : []
  const callSection =
    sections.find((section) => section?.id === 'call') || {
      id: 'call',
      label: 'Call',
      route: SECTION_ROUTES.call,
      clients: [],
    }
  const whatsappSection =
    sections.find((section) => section?.id === 'whatsapp') || {
      id: 'whatsapp',
      label: 'Whatsapp',
      route: SECTION_ROUTES.whatsapp,
      clients: [],
    }
  const currentSection =
    currentView === 'call' ? callSection : currentView === 'whatsapp' ? whatsappSection : null
  const tabs = [
    { value: 'overview', label: 'Overview', route: entryPoint?.route || SECTION_ROUTES.overview },
    { value: 'call', label: callSection?.label || 'Call', route: callSection?.route || SECTION_ROUTES.call },
    {
      value: 'whatsapp',
      label: whatsappSection?.label || 'Whatsapp',
      route: whatsappSection?.route || SECTION_ROUTES.whatsapp,
    },
  ]
  const currentCopy = getSectionCopy(currentView)

  useEffect(() => {
    let active = true

    async function loadBootstrap() {
      setLoading(true)
      setLoadError('')

      const result = await getSupportCustomCommunicationsBootstrap()
      if (!active) {
        return
      }

      if (result?.ok) {
        const nextBootstrap = result?.data || null
        setBootstrap(nextBootstrap)
        setFormsByClientId((previous) => {
          const nextValue = { ...previous }
          ;(nextBootstrap?.sections || []).forEach((section) => {
            ;(section?.clients || []).forEach((client) => {
              if (!nextValue[client?.id]) {
                nextValue[client.id] = { ...EMPTY_FORM }
              }
            })
          })
          return nextValue
        })
      } else {
        setLoadError(result?.message || 'Unable to load custom communications.')
      }

      setLoading(false)
    }

    loadBootstrap()

    return () => {
      active = false
    }
  }, [])

  const handleFieldChange = (clientId, field, value) => {
    setFormsByClientId((previous) => ({
      ...previous,
      [clientId]: {
        ...(previous?.[clientId] || EMPTY_FORM),
        [field]: value,
      },
    }))
  }

  const handleSend = async (client) => {
    const form = formsByClientId?.[client?.id] || EMPTY_FORM
    const toPhoneNumber = String(form?.toPhoneNumber || '').trim()
    const message = String(form?.message || '').trim()

    if (!hasValidPhoneNumber(toPhoneNumber)) {
      toast.error('Enter a valid phone number before sending.')
      return
    }
    if (!message) {
      toast.error('Enter a message before sending.')
      return
    }
    if (!client?.sendPath) {
      toast.error('This client is missing a send path.')
      return
    }

    setSendingClientId(client.id)
    const payload = {
      toPhoneNumber,
      message,
      ...(currentSection?.id === 'call'
        ? { languageCode: form?.languageCode || client?.defaultLanguageCode || 'auto' }
        : {}),
    }
    const result = await dispatchSupportCustomCommunication(client.sendPath, payload)

    if (result?.ok) {
      setResultsByClientId((previous) => ({
        ...previous,
        [client.id]: result?.data || null,
      }))
      setFormsByClientId((previous) => ({
        ...previous,
        [client.id]: {
          ...(previous?.[client.id] || EMPTY_FORM),
          toPhoneNumber,
          message,
        },
      }))
      if (currentSection?.id === 'call') {
        toast.success(`Outcall queued via ${client?.label || 'the selected client'}.`)
      } else {
        const deliveryMode = result?.data?.deliveryMode || 'selected'
        toast.success(`Whatsapp message sent via ${client?.label || 'the selected sender'} (${deliveryMode}).`)
      }
    } else {
      toast.error(result?.message || 'Unable to send custom communication.')
    }

    setSendingClientId(null)
  }

  return (
    <>
      <SupportHeader
        heading={entryPoint?.label || 'Custom Communications'}
        subHeading={currentView === 'overview' ? 'Important Actions' : currentCopy.title}
        showBackButton
      />
      <Box
        sx={{
          width: '100%',
          minHeight: 'calc(100vh - 64px)',
          padding: { xs: '24px 20px', sm: '28px 24px', md: '32px 28px' },
          background:
            'linear-gradient(180deg, rgba(250, 245, 255, 0.68) 0%, rgba(244, 247, 255, 0.72) 45%, rgba(241, 245, 249, 0.92) 100%)',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3 },
            borderRadius: 4,
            border: '1px solid',
            borderColor: alpha('#7c3aed', 0.14),
            background:
              'linear-gradient(135deg, rgba(124, 58, 237, 0.12) 0%, rgba(29, 78, 216, 0.08) 40%, rgba(255, 255, 255, 0.96) 100%)',
            boxShadow: '0 20px 44px rgba(76, 29, 149, 0.08)',
            mb: 3,
          }}
        >
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Stack spacing={1.25} sx={{ maxWidth: 860 }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 3,
                      display: 'grid',
                      placeItems: 'center',
                      color: '#fff',
                      background:
                        'linear-gradient(135deg, rgba(124, 58, 237, 0.94) 0%, rgba(30, 64, 175, 0.9) 100%)',
                      boxShadow: '0 14px 30px rgba(76, 29, 149, 0.18)',
                    }}
                  >
                    {getSectionIcon(currentView, { fontSize: 26 })}
                  </Box>
                  <Stack spacing={0.5}>
                    <Typography variant="overline" sx={{ letterSpacing: 1.1, color: '#6d28d9', fontWeight: 700 }}>
                      Support Home / Important Actions
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                      {currentCopy.title}
                    </Typography>
                  </Stack>
                </Stack>
                <Typography variant="body1" color="text.secondary">
                  {currentView === 'overview'
                    ? entryPoint?.summary || currentCopy.description
                    : currentCopy.description}
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Chip
                  size="small"
                  label="ROLE_MANAGEMENT"
                  sx={{
                    fontWeight: 700,
                    backgroundColor: alpha('#7c3aed', 0.12),
                    color: '#6d28d9',
                  }}
                />
                <Chip size="small" label={`${getConfiguredClientCount(callSection)}/${(callSection?.clients || []).length} call clients ready`} />
                <Chip size="small" label={`${getConfiguredClientCount(whatsappSection)}/${(whatsappSection?.clients || []).length} WhatsApp senders ready`} />
              </Stack>
            </Stack>

            <Tabs
              value={currentView}
              onChange={(_, nextValue) => {
                const nextTab = tabs.find((tab) => tab.value === nextValue)
                navigate(nextTab?.route || SECTION_ROUTES[nextValue] || SECTION_ROUTES.overview)
              }}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  minHeight: 44,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 999,
                  mr: 1,
                },
              }}
            >
              {tabs.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
          </Stack>
        </Paper>

        {loading ? (
          <Paper
            sx={{
              borderRadius: 4,
              p: 5,
              display: 'grid',
              placeItems: 'center',
              minHeight: 260,
            }}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading custom communication clients...
              </Typography>
            </Stack>
          </Paper>
        ) : loadError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                Retry
              </Button>
            }
            sx={{ borderRadius: 3 }}
          >
            {loadError}
          </Alert>
        ) : currentView === 'overview' ? (
          <Stack spacing={2.5}>
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Use <strong>Call</strong> for ad hoc voice outcalls and <strong>Whatsapp</strong> for text sends from the configured patient or mentor/support Twilio numbers.
            </Alert>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' },
                gap: 2.5,
              }}
            >
              {[callSection, whatsappSection].map((section) => (
                <OverviewSectionCard
                  key={section?.id}
                  section={section}
                  onOpen={() => navigate(section?.route || SECTION_ROUTES[section?.id] || ENTRY_ROUTE)}
                />
              ))}
            </Box>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              {currentSection?.id === 'call'
                ? 'Each client uses the same free-text message as the spoken outcall body.'
                : 'Each WhatsApp sender follows the backend warm-window check automatically and falls back to the approved Twilio template when the channel is cold.'}
            </Alert>

            {(currentSection?.clients || []).length === 0 ? (
              <Alert severity="warning" sx={{ borderRadius: 3 }}>
                No {currentSection?.label || 'communication'} clients are available in the bootstrap payload yet.
              </Alert>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                  gap: 2.5,
                }}
              >
                {(currentSection?.clients || []).map((client) => (
                  <ClientCard
                    key={client?.id}
                    client={client}
                    form={formsByClientId?.[client?.id] || EMPTY_FORM}
                    sectionId={currentSection?.id}
                    sending={sendingClientId === client?.id}
                    dispatch={resultsByClientId?.[client?.id] || null}
                    onFieldChange={handleFieldChange}
                    onSubmit={handleSend}
                  />
                ))}
              </Box>
            )}
          </Stack>
        )}
      </Box>
    </>
  )
}
